import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";

/**
 * Service to call HTTP request via Axios
 */
const UserApiService = {
  URL: "",
  async init() {
    Vue.use(VueAxios, axios);
    const resp = await Vue.axios.get(`api/url/get-user-service-uri`);
    this.URL = resp.data;
  },

  query(resource, params) {
    let config = {
      params
    };

    return Vue.axios.get(this.URL + resource, config).catch(error => {
      // console.log(error);
      throw new Error(`[KT] UserApiService ${error}`);
    });
  },

  API_CATEGORIES: "/admin/categories",

  /**
   * Send the GET HTTP request
   * @param resource
   * @param slug
   * @returns {*}
   */
  async get(resource, slug = "") {
    return Vue.axios.get(this.URL + `${resource}/${slug}`).catch(error => {
      throw new Error(`[KT] UserApiService ${error}`);
    });
  },

  getFile(resource, slug = "") {
    return Vue.axios
      .get(this.URL + `${resource}/${slug}`, {
        responseType: "blob"
      })
      .catch(error => {
        throw new Error(`[KT] UserApiService ${error}`);
      });
  },
  /**
   * Set the POST HTTP request
   * @param resource
   * @param params
   * @returns {*}
   */ post(resource, params) {
    return Vue.axios.post(this.URL + `${resource}`, params);
  },

  /**
   * Send the UPDATE HTTP request
   * @param resource
   * @param slug
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  update(resource, slug, params) {
    return Vue.axios.put(this.URL + `${resource}/${slug}`, params);
  },

  patch(resource, params, config = {}) {
    return Vue.axios.patch(this.URL + `${resource}`, params, {
      headers: { "Content-Type": "application/json-patch+json" },
      ...config
    });
  },

  /**
   * Send the PUT HTTP request
   * @param resource
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  put(resource, params) {
    return Vue.axios.put(this.URL + `${resource}`, params);
  },

  /**
   * Send the DELETE HTTP request
   * @param resource
   * @returns {*}
   */
  delete(userId) {
    return Vue.axios.delete(`${this.URL}/admin/${userId}`).catch(error => {
      // console.log(error);
      throw new Error(`[RWV] UserApiService ${error}`);
    });
  },

  /**
   * Get categories
   */
  async getCategories() {
    try {
      return await Vue.axios.get(this.URL + this.API_CATEGORIES);
    } catch (error) {
      this.$log.error("Error: ", error);
      throw new Error(`[RWV] UserApiService ${error}`);
    }
  },

  API_ONBOARDING_PARTNERS: "/subscription/admin/onboarding-partners/list",

  /**
   * Get onboarding partners with subscription data
   * @param {Object} params - Query parameters for filtering partners
   * @returns {Promise} - Promise resolving to partners data
   */
  async getOnboardingPartners(params = {}) {
    try {
      // Ensure URL is initialized before making the request
      if (!this.URL) {
        await this.init();
      }

      // Set default parameters for onboarding partners
      const defaultParams = {
        ...params
      };

      return await Vue.axios.get(this.URL + this.API_ONBOARDING_PARTNERS, {
        params: defaultParams,
        paramsSerializer: this.paramsSerializer
      });
    } catch (error) {
      console.error("Error fetching onboarding partners: ", error);
      throw new Error(`[RWV] UserApiService ${error}`);
    }
  },

  async getOnboardingPartnerFilterTypes() {
    try {
      // Ensure URL is initialized before making the request
      if (!this.URL) {
        await this.init();
      }

      return await Vue.axios.get(
        this.URL + "/subscription/admin/onboarding-partners/onboard-types"
      );
    } catch (error) {
      console.error("Error fetching onboarding partners count: ", error);
      throw new Error(`[RWV] UserApiService ${error}`);
    }
  },

  async exportOnboardingPartners(params = {}) {
    try {
      // Ensure URL is initialized before making the request
      if (!this.URL) {
        await this.init();
      }

      // Set default parameters for onboarding partners
      const defaultParams = {
        ...params
      };

      return await Vue.axios.get(`${this.URL}/subscription/admin/onboarding-partners/report`, {
        params: defaultParams,
        paramsSerializer: this.paramsSerializer,
        responseType: "blob"
      });
    } catch (error) {
      console.error("Error exporting onboarding partners: ", error);
      throw new Error(`[RWV] UserApiService ${error}`);
    }
  },

  paramsSerializer: function(params) {
    const searchParams = new URLSearchParams();
    for (const key in params) {
      const value = params[key];
      if (Array.isArray(value)) {
        // For arrays, add each value as a separate parameter
        value.forEach(item => searchParams.append(key, item));
      } else if (value !== null && value !== undefined && value !== "") {
        searchParams.append(key, value);
      }
    }
    return searchParams.toString();
  },

  API_DISPOSABLE_DOMAINS: "/admin/disposable-domains",
  API_EMAIL_ALLOWLIST: "/admin/email-allowlist",

  async listDisposableDomains(source) {
    if (!this.URL) {
      await this.init();
    }
    const config = source ? { params: { source } } : {};
    return Vue.axios.get(this.URL + this.API_DISPOSABLE_DOMAINS, config);
  },

  async addDisposableDomain(payload) {
    if (!this.URL) {
      await this.init();
    }
    return Vue.axios.post(this.URL + this.API_DISPOSABLE_DOMAINS, payload);
  },

  async deleteDisposableDomain(domain) {
    if (!this.URL) {
      await this.init();
    }
    return Vue.axios.delete(
      `${this.URL}${this.API_DISPOSABLE_DOMAINS}/${encodeURIComponent(domain)}`
    );
  },

  async listEmailAllowlist() {
    if (!this.URL) {
      await this.init();
    }
    return Vue.axios.get(this.URL + this.API_EMAIL_ALLOWLIST);
  },

  async addEmailAllowlistEntry(payload) {
    if (!this.URL) {
      await this.init();
    }
    return Vue.axios.post(this.URL + this.API_EMAIL_ALLOWLIST, payload);
  },

  async deleteEmailAllowlistEntry(domain) {
    if (!this.URL) {
      await this.init();
    }
    return Vue.axios.delete(`${this.URL}${this.API_EMAIL_ALLOWLIST}/${encodeURIComponent(domain)}`);
  }
};

export default UserApiService;
